import {
	MimeTypeExtended,
	NativeObjectLocation,
	NativeSelectedFile,
	NativeUploadReconciliationBatch,
	UploadBatchSelection,
	UploadFailureReason,
	UploadModel,
	UploadProgressMessage,
	UploadProgressPhoto,
	UploadProgressStage,
} from '@sosocio/creator-types/app';
import * as DB from '@sosocio/creator-types/database';
import * as PI from '@sosocio/creator-types/project';
import ProductState from 'classes/productstate';
import EventBus from 'components/event-bus';
import analytics from 'controllers/analytics';
import awsCognito from 'controllers/aws-cognito';
import experiment from 'controllers/experiment';
import filesizeParser from 'filesize-parser';
import moment from 'moment';
import { nanoid } from 'nanoid';
import * as DialogService from 'services/dialog';
import {
	AwsAutoExpireUploader,
	buildAutoExpireObjectKey,
	createUploadDiagnosticMetadata,
	createUploadInspector,
	detectImageDetails,
	getUploadBatchProgress,
	type ImageDetails,
	type MimeResolutionSource,
	NonRetryableUploadProcessingError,
	projectUploadModuleState,
	resolveFileType,
	type UploadError,
	type UploadFileAdapterLifecycle,
	type UploadSessionAdapters,
	uploadSessionMachine,
	type UploadSessionTransport,
} from 'services/upload';
import {
	createDesignPrintUploadContext,
	getDesignPrintTargetPageIds,
	getDesignPrintUploadOutputs,
} from 'services/upload/design-print-pages';
import {
	ExternalUploadController,
	type LocalUploadProgressContext,
} from 'services/upload/external-upload-controller';
import {
	type FileConversionWaitOptions,
	waitForFileConversion,
} from 'services/upload/file-conversion-dialog';
import {
	ERRORS_UPLOAD_REJECTED_TOO_MANY,
	UnsavedPhotoModelsError,
	UnsupportedFileTypeError,
} from 'settings/errors';
import {
	getAcceptedUploadFileTypes,
	MIME_TYPES_BROWSER_SUPPORTED,
	MIME_TYPES_RASTER_IMAGES,
	MIME_TYPES_REQUIRE_CONVERSION,
	MIME_TYPES_SVG,
	MIME_TYPES_WITH_EMBEDDED_IMAGES,
} from 'settings/filetypes';
import { OfferingGroups } from 'settings/offerings';
import store, {
	AppDataModule,
	AppStateModule,
	ConfigModule,
	PhotosModule,
	ProductStateModule,
	ThemeStateModule,
	UploadModule,
} from 'store';
import detectUpload from 'tools/detect-upload';
import { getOfferingModelIsFullColorLogoProduct } from 'tools/project/offering';
import _ from 'underscore';
import type { ImageHeaderDiagnostics } from 'utils/inspect-image-header';
import reportError from 'utils/report-error';
import FileConversionView from 'views/file-conversion';
import UploadProgressView from 'views/upload-progress';
import UploadQrView from 'views/upload-qr';
import VectorizationView from 'views/vectorization';
import { createActor } from 'xstate';

const fullUploadSize = {
	width: 9000,
	height: 9000,
};
export const UPLOAD_CONVERSION_COMPLETED_EVENT = 'upload conversion completed';

/**
 * Leave bounded upload context in Bugsnag without allowing telemetry failures
 * to interrupt upload control flow.
 *
 * @param message Stable breadcrumb name describing the upload event.
 * @param metadata Explicitly allowlisted scalar upload context.
 */
function leaveUploadBreadcrumb(
	message: string,
	metadata: Record<string, unknown>,
): void {
	if (
		typeof window === 'undefined'
		|| typeof window.glBugsnagClient === 'undefined'
	) {
		return;
	}

	try {
		window.glBugsnagClient.leaveBreadcrumb(
			message,
			metadata,
			'state',
		);
	} catch {
		/**
		 * Telemetry is best-effort and must not affect the upload lifecycle.
		 */
	}
}

/** Maps item-machine errors onto the bounded, privacy-safe lifecycle taxonomy. */
function toUploadFailureReason(failure: UploadError | null): UploadFailureReason {
	const kind = failure?.kind === 'cancelled'
		? failure.cause?.kind
		: failure?.kind;

	if (kind === 'validation') {
		return 'validation';
	}

	if (kind === 'processing') {
		return 'processing';
	}

	if (kind === 'upload') {
		return 'network';
	}

	return 'unknown';
}

// Maximum number of file names listed per reason in the skipped-files dialog
const SKIPPED_FILES_LISTED = 5;

interface SkippedFiles {
	duplicate: string[];
	size: string[];
	unsupported: string[];
}

function createSkippedFiles(): SkippedFiles {
	return {
		duplicate: [],
		size: [],
		unsupported: [],
	};
}

/**
 * Build the skipped-files dialog text. File names are appended outside the
 * translation so i18next's HTML escaping does not alter them; the dialog
 * renders the body as preformatted text.
 *
 * @param skippedFiles File names grouped by skip reason.
 * @param sizeLimit Human-readable upload size limit.
 * @returns Dialog text, or an empty string when no file was skipped.
 */
function formatSkippedFiles(
	skippedFiles: SkippedFiles,
	sizeLimit: string,
): string {
	const groups: Array<[string[], string]> = [
		[
			skippedFiles.unsupported,
			window.App.router.$t('uploadSkippedUnsupported'),
		],
		[
			skippedFiles.duplicate,
			window.App.router.$t('uploadSkippedDuplicate'),
		],
		[
			skippedFiles.size,
			window.App.router.$t(
				'uploadSkippedSize',
				{
					sizeLimit,
				},
			),
		],
	];

	const sections = groups
		.filter(([names]) => names.length > 0)
		.map(([
			names,
			heading,
		]) => {
			const lines = [
				heading,
				...names.slice(
					0,
					SKIPPED_FILES_LISTED,
				),
			];

			if (names.length > SKIPPED_FILES_LISTED) {
				lines.push(window.App.router.$t(
					'uploadSkippedMore',
					{
						count: names.length - SKIPPED_FILES_LISTED,
					},
				));
			}

			return lines.join('\n');
		});

	if (sections.length === 0) {
		return '';
	}

	return [
		window.App.router.$t('uploadSkippedIntro'),
		...sections,
	].join('\n\n');
}

interface FileData {
	file: File;
	fileType: MimeTypeExtended;
	date: number;
	headerDiagnostics?: ImageHeaderDiagnostics;
	mimeSource: MimeResolutionSource;
	sniffedMime?: string;
}

interface FileTypeMap {
	file: File;
	fileName: string;
	fileType: MimeTypeExtended;
	headerDiagnostics?: ImageHeaderDiagnostics;
	id: string;
	lastModified: number;
	mimeSource: MimeResolutionSource;
	size: number;
	/**
	 * Set when the selected file cannot safely provide a browser preview because
	 * inspection failed or its format requires server conversion. `getLocalFile`
	 * withholds the raw copy while remote processing is pending.
	 */
	previewUnusable?: boolean;
	sniffedMime?: string;
}

interface NativeAcceptedSelection {
	photoModel: PI.PhotoModel;
	totalBytes: number;
	uploadId: string;
	uploadStart: number;
}

type UploadSessionActor = ReturnType<typeof createActor<typeof uploadSessionMachine>>;

class Upload {
	// The DOM element this class will be attached to
	private buttonEl: HTMLElement | null = null;

	private bucketModel: DB.BucketModel | undefined;

	// Check for maximum photos of product/theme when uploading?
	private checkMaxPhotos = true;

	// Store array of locally uploaded photos that have completed processing
	private completedPhotoModels: PI.PhotoModel[] = [];

	private completedPhotoModelsByUploadId = new Map<string, PI.PhotoModel>();

	private awsUploader: AwsAutoExpireUploader | undefined;

	// Flags for experimental features
	public features: {
		scaling: boolean;
	} = {
		scaling: false,
	};

	private fileTypeMapper: FileTypeMap[] = [];

	/** Destination captured by the editor before opening an upload interaction. */
	private designPrintUploadTarget: {
		pageId: PI.PageModel['id'];
		targetPageObjectId?: PI.PageObjectModel['id'];
	} | undefined;

	// Reference to the hidden input field that is used for trigger the file upload UI
	private inputField: HTMLInputElement | undefined;

	// Count number of uploads that are rejected due to a too large batch size
	private rejectBatchSizeCount = 0;

	// Names of files skipped by validation in the current batch, grouped by reason
	private skippedFiles: SkippedFiles = createSkippedFiles();

	/*
	 * Size limit of files to upload in MB
	 * IMPORTANT: Changing the sizeLimit requires the same change in webhook/s3/cors,
	 * otherwise the policy document will be invalid
	 * 20 mB (1 mB = 1000 * 1024 bytes)
	 */
	private sizeLimit = '20MB';

	// Counter for number of (validated) photos submitted to upload controller
	private submitCount = 0;

	private trackFilesChange = false;

	private readonly externalUpload = new ExternalUploadController({
		appVersion: VERSION,
		getSession: () => this.getSessionActor(),
		getPlatform: () => window.glPlatform,
		onCancelAll: () => this.reset(),
		trackLifecycleEvent: (properties) => {
			analytics.trackEvent(
				'Upload lifecycle',
				{
					...properties,
				},
				{
					amplitude: true,
					gtm: false,
					moengage: false,
					segment: false,
				},
			);
		},
		dialogs: {
			uploadProgress: UploadProgressView,
			uploadQr: UploadQrView,
		},
	});

	private maxConcurrentWebUploads = 2;

	private nativeUploadLifecycles = new Map<string, UploadFileAdapterLifecycle>();

	private sessionActor: UploadSessionActor | undefined;

	// Handle of the active upload-progress polling interval (uploadSubmitted)
	private progressPollHandle: number | undefined;

	private progressBatchGeneration = 0;

	private progressBatchTransferredBaseline = 0;

	private progressBatchUploadIds = new Set<string>();

	private progressFinalizationActive = false;

	private validationDialog: ReturnType<typeof DialogService.openProgressDialog> | undefined;

	public init() {
		this.bucketModel = AppDataModule.getBucket('auto-expire');

		if (window.glPlatform === 'native') {
			this.setupNativeBridge();
		} else if (!detectUpload()) {
			return;
		} else {
			this.setupWeb();
		}

		this.getSessionActor();

		if (window.glPlatform === 'native') {
			void this.externalUpload
				.reconcileNativeUploads()
				.then((reconciledBatches) => {
					if (!AppStateModule.uploadOnly || reconciledBatches === null) {
						return;
					}

					reconciledBatches.forEach((batch) => {
						this.resumeNativeBatchProgress(batch);
					});
				})
				.catch((error) => {
					reportError(
						error,
						'warning',
					);
				});
		}

		/*
		 * store.watch delegates to Vue's watch and we pass no `immediate` option,
		 * so this callback only runs on subsequent online/offline transitions —
		 * it does NOT fire on registration. retryAll() is therefore safe here and
		 * will not be invoked against an empty/initial state at startup.
		 */
		store.watch(
			(state) => state.appstate.online,
			(online) => {
				this.getSessionActor().send({
					type: online ? 'session.network.online' : 'session.network.offline',
				});

				if (online) {
					this.retryAll();
				}
			},
		);
	}

	public changeCheckMaxPhotos(newValue: boolean) {
		this.checkMaxPhotos = newValue;
	}

	public dropFiles(
		event: DragEvent,
	) {
		if (event.dataTransfer) {
			const { files } = event.dataTransfer;

			if (files.length) {
				analytics.trackEvent(
					'drop files',
					{
						quantity: files.length,
					},
				);

				// Use DataTransfer interface to access the file(s)
				this.addFiles(files);
			}
		}
	}

	/**
	 * Validate an input-file batch and preserve its initial design-print destinations.
	 *
	 * @param selectedFiles Files supplied by the picker or drag/drop interaction.
	 */
	private addFiles(
		selectedFiles: FileList,
	): void {
		const designPrint = this.getDesignPrintUploadContext(selectedFiles.length);
		// Kept index-aligned with `promises` so each rejection maps back to its file
		const candidates: File[] = [];
		const promises: Promise<FileData>[] = [];
		for (let i = 0; i < selectedFiles?.length; i += 1) {
			const file = selectedFiles?.item(i);

			if (file) {
				// Check if this file was already submitted previously
				const mapItem = this.fileTypeMapper.find(
					(m) => m.fileName == file.name
						&& m.lastModified == file.lastModified
						&& m.size == file.size,
				);

				if (mapItem) {
					this.skippedFiles.duplicate.push(file.name);
				} else if (file.size >= filesizeParser(this.sizeLimit)) {
					this.skippedFiles.size.push(file.name);
				} else {
					candidates.push(file);
					promises.push(
						this.resolveFileData(file),
					);
				}
			}
		}

		Promise
			.allSettled(promises)
			.then((results) => {
				const acceptedFiles: FileData[] = [];

				results.forEach((result, index) => {
					if (result.status == 'fulfilled') {
						acceptedFiles.push(result.value);
					} else if (result.reason instanceof UnsupportedFileTypeError) {
						this.skippedFiles.unsupported.push(candidates[index].name);
					} else {
						reportError(
							result.reason instanceof Error
								? result.reason
								: new Error(String(result.reason)),
							'error',
						);
					}
				});

				// We sort the images by date
				acceptedFiles.sort(
					(a, b) => a.date - b.date,
				);

				/*
				 * Now we prioritize file formats that need conversion on the server, to minize waiting for upload process
				 * (we can't show previews for these filetypes, so those need to be fully processed before shown to user)
				 */
				acceptedFiles.sort(
					(a, b) => {
						if (
							MIME_TYPES_REQUIRE_CONVERSION.includes(a.fileType)
							&& MIME_TYPES_BROWSER_SUPPORTED.includes(b.fileType)
						) {
							return -1;
						}

						if (
							MIME_TYPES_REQUIRE_CONVERSION.includes(b.fileType)
							&& MIME_TYPES_BROWSER_SUPPORTED.includes(a.fileType)
						) {
							return 1;
						}

						return 0;
					},
				);

				/*
				 * The actual number of rejected files is decided per-item by
				 * addUploadToProduct (the selection can also change underneath us
				 * while the batch processes), so we count the real rejections in
				 * the catch below instead of an up-front estimate. Reset the count
				 * for this batch here so it is a single source of truth.
				 */
				this.rejectBatchSizeCount = 0;

				/*
				 * All selected files are now ready for uploading and processing
				 * so we call the submit event (other parts of the app can listen to this)
				 */
				this.uploadSubmit(acceptedFiles.length);

				// We now want to process the items sequentially to prevent browser crash/freeze
				const toUpload: UploadModel[] = [];
				const doNextItem = (i: number) => {
					const { file } = acceptedFiles[i];
					this
						.addUploadToProduct(
							file,
							designPrint,
						)
						.then((uploadModel) => {
							const dialogProgressComponent = this.validationDialog?.api.bodyComponent();

							if (dialogProgressComponent) {
								dialogProgressComponent.value += 1;
							}

							toUpload.push(uploadModel);
						})
						.catch((e: unknown) => {
							const error = e instanceof Error ? e : new Error(String(e));

							if (error.message === ERRORS_UPLOAD_REJECTED_TOO_MANY) {
								// Intended behavior, too many photos selected.
								this.rejectBatchSizeCount += 1;
							} else {
								/*
								 * A genuine per-file failure (image detection, photo
								 * selection, etc.). Rethrowing here escaped as an
								 * unhandled rejection and silently dropped the file, so
								 * surface it to Bugsnag the same way other per-file
								 * failures do and let the batch continue.
								 */
								const failedMapItem = this.fileTypeMapper.find((mapItem) => (
									mapItem.file === file
								));

								reportError(
									error,
									'error',
									(event) => {
										event.addMetadata(
											'uploadData',
											createUploadDiagnosticMetadata(
												undefined,
												{
													failureKind: 'preview',
													resolvedMime: failedMapItem?.fileType,
													retryable: false,
													sizeBytes: file.size,
													stage: 'previewing',
													transport: 'web',
													uploadId: (
														failedMapItem?.id
														?? 'unavailable'
													),
												},
											),
										);
									},
								);
							}
						})
						.finally(() => {
							i += 1;

							if (i < acceptedFiles.length) {
								doNextItem(i);
							} else {
								toUpload.forEach((uploadModel) => {
									this.uploadFile(uploadModel);
								});

								this.uploadSubmitted(toUpload.length);
								const offeringModel = ProductStateModule.getOffering;
								const isFullColorLogoProduct = getOfferingModelIsFullColorLogoProduct(offeringModel);

								this
									.waitForFileConversion({
										vectorizationVariant: (
											isFullColorLogoProduct
												? 'analyzing'
												: 'optimizing'
										),
									})
									.then((status) => {
										EventBus.emit(
											UPLOAD_CONVERSION_COMPLETED_EVENT,
											{
												status,
											},
										);
									});
							}
						});
				};

				if (acceptedFiles.length === 0) {
					this.uploadSubmitted(0);
				} else {
					doNextItem(0);
				}
			});
	}

	/**
	 * Register one local source with the processing options of its original batch.
	 *
	 * @param file Source file to inspect and register.
	 * @param designPrint Frozen destination metadata for a design-print input.
	 * @returns Upload model submitted to the file-transfer actor.
	 */
	private addUploadToProduct(
		file: File,
		designPrint?: PI.PhotoModel['_designPrint'],
	): Promise<UploadModel> {
		const mapItem = this.fileTypeMapper.find(
			(m) => m.fileName == file.name
				&& m.lastModified == file.lastModified
				&& m.size == file.size,
		);

		if (!mapItem) {
			return Promise.reject(
				new Error('Could not find upload model'),
			);
		}

		const x = ProductStateModule.getPhotosSelected.length;
		const { themeModel } = ThemeStateModule;
		const maxphotos = !themeModel || _.isNull(themeModel.maxphotos)
			? 200
			: themeModel.maxphotos;

		if (this.checkMaxPhotos
			&& x >= maxphotos
		) {
			// Remove item from fileTypeMapper, since this file will not be part of the project selection
			const i = this.fileTypeMapper.findIndex(
				(m) => m.id == mapItem.id,
			);

			if (i >= 0) {
				this.fileTypeMapper.splice(
					i,
					1,
				);
			}

			/*
			 * We are already at the limit
			 * so we reject adding the photo
			 */
			return Promise.reject(
				new Error(ERRORS_UPLOAD_REJECTED_TOO_MANY),
			);
		}

		return this
			.getImageDetails(
				file,
				mapItem,
			)
			.then((data) => {
				/*
				 * Carry the unusable local-preview signal so rendering withholds the
				 * raw copy while the upload pipeline resolves the processed model.
				 */
				mapItem.previewUnusable = data.previewUnusable;

				const offeringModel = ProductStateModule.getOffering;
				const type = offeringModel?.type || 'photo';
				const isLogoProduct = offeringModel?.type === 'logo';
				const isFullColorLogoProduct = getOfferingModelIsFullColorLogoProduct(offeringModel);
				const shouldVectorizeOnUpload = (
					ConfigModule['features.vectorization']
					&& isLogoProduct
					&& !MIME_TYPES_SVG.includes(mapItem.fileType)
					&& (
						MIME_TYPES_RASTER_IMAGES.includes(mapItem.fileType)
						|| MIME_TYPES_WITH_EMBEDDED_IMAGES.includes(mapItem.fileType)
					)
				);
				const shouldVectorizeWithOfferingSettings = (
					offeringModel?.applyVectorization
					&& ConfigModule['features.vectorization']
					&& !isFullColorLogoProduct
					&& !MIME_TYPES_SVG.includes(mapItem.fileType)
					&& (
						MIME_TYPES_RASTER_IMAGES.includes(mapItem.fileType)
						|| MIME_TYPES_WITH_EMBEDDED_IMAGES.includes(mapItem.fileType)
					)
				);

				const photoAttributes: Partial<PI.PhotoModel> = {
					_designPrint: designPrint,
					_localRef: mapItem.id,
					_orientation: data.orientation,
					_type: mapItem.fileType,
					source: 'upload',
					externalId: mapItem.id,
					full_width: data.fullWidth,
					full_height: data.fullHeight,
					thumb_url: data.thumbUrl,
					photodate: data.photodate,
					fcx: data.fcx,
					fcy: data.fcy,
					fcw: data.fcw,
					fch: data.fch,
					type,
				};

				if (
					shouldVectorizeOnUpload
					|| shouldVectorizeWithOfferingSettings
				) {
					photoAttributes._vectorize = {
						maxColors: ConfigModule['vectorization.maxColors'],
					};

					if (
						isFullColorLogoProduct
						&& ConfigModule['features.vectorizationPhotoDetection']
					) {
						photoAttributes._vectorize.skipVectorizationIfPhoto = true;
					} else if (
						isLogoProduct
						&& ConfigModule['features.vectorizationPhotoDetection']
					) {
						photoAttributes._vectorize.errorIfPhoto = true;
					}
				}

				return ProductStateModule.selectPhoto(photoAttributes);
			})
			.then((photoModel) => {
				const uploadModel: UploadModel = {
					_type: mapItem.fileType,
					id: mapItem.id,
					errorCount: 0,
					name: mapItem.fileName,
					overall: 1,
					photoModelId: photoModel.id,
					queue: 0,
					uploadEnd: 0,
					uploadStart: 0,
					totalBytes: mapItem.size,
					width: photoModel.full_width,
					height: photoModel.full_height,
				};

				return uploadModel;
			});
	}

	private closeValidationDialog() {
		this.validationDialog?.close();
		this.validationDialog = undefined;
	}

	private clearProgressPoll() {
		if (this.progressPollHandle !== undefined) {
			window.clearInterval(this.progressPollHandle);
			this.progressPollHandle = undefined;
		}
	}

	/**
	 * Registers a web upload with the active progress batch before starting its
	 * item actor, so even a synchronous completion is attributed correctly.
	 *
	 * @param uploadModel Validated upload metadata for the selected file.
	 */
	private uploadFile(
		uploadModel: UploadModel,
	): void {
		this.progressBatchUploadIds.add(uploadModel.id);
		this.getSessionActor().send({
			type: 'session.item.add',
			item: {
				input: {
					uploadId: uploadModel.id,
					name: uploadModel.name,
					totalBytes: uploadModel.totalBytes,
					transport: 'web',
					localPhotoId: (
						typeof uploadModel.photoModelId === 'string'
							? uploadModel.photoModelId
							: undefined
					),
				},
				transport: 'web',
				metadata: {
					type: uploadModel._type,
					width: uploadModel.width,
					height: uploadModel.height,
					uploadStart: uploadModel.uploadStart,
					uploadEnd: uploadModel.uploadEnd,
					url: uploadModel.url,
					photoModelId: uploadModel.photoModelId,
				},
			},
		});
	}

	private buildSessionAdapters(): UploadSessionAdapters {
		return {
			createLocalPhoto: (context) => {
				if (!context.localPhotoId) {
					throw new Error(
						context.transport === 'native'
							? 'Missing local photo model for native upload'
							: 'Missing local photo model for upload',
					);
				}

				return {
					localPhotoId: context.localPhotoId,
				};
			},
			uploadFile: (context, lifecycle) => {
				if (context.transport === 'native') {
					return this.runNativeUploadAdapter(
						context.uploadId,
						lifecycle,
					);
				}

				return this.runWebUploadAdapter(
					context.uploadId,
					lifecycle,
				);
			},
			processUploadedPhoto: (context) => this.completeUploadProcessing(
				context.uploadId,
				context.uploadUrl,
			),
		};
	}

	private getSessionActor(): UploadSessionActor {
		if (this.sessionActor) {
			return this.sessionActor;
		}

		/*
		 * Transition breadcrumbs make a stuck upload reconstructable: every
		 * state change across the session and its children lands in
		 * Bugsnag's breadcrumb trail, so the existing notify() call sites
		 * carry the event history that led to the failure. Observe-only by
		 * design.
		 */
		const inspector = createUploadInspector({
			onBreadcrumb: (breadcrumb) => {
				leaveUploadBreadcrumb(
					'upload transition',
					{
						actorId: breadcrumb.actorId,
						event: breadcrumb.eventType,
						state: breadcrumb.state,
					},
				);
			},
		});

		const actor = createActor(
			uploadSessionMachine,
			{
				input: {
					online: AppStateModule.online,
					maxConcurrentWebUploads: this.maxConcurrentWebUploads,
					adapters: this.buildSessionAdapters(),
				},
				inspect: inspector.inspect,
			},
		);

		const syncProjection = () => {
			UploadModule.setProjection(projectUploadModuleState(actor.getSnapshot().context));
		};

		actor.subscribe(syncProjection);
		actor.on(
			'session.item.projection',
			syncProjection,
		);
		actor.on(
			'session.item.failed',
			(event) => {
				this.handleUploadFailure(
					event.transport,
					event.uploadId,
					event.error,
				);
			},
		);
		actor.on(
			'session.item.processingFailed',
			(event) => {
				this.handleUploadProcessingFailure(
					event.transport,
					event.uploadId,
					event.error,
				);
			},
		);
		actor.on(
			'session.item.terminated',
			(event) => {
				if (event.reason === 'saved') {
					syncProjection();
					this.completeSavedSessionItem(event.uploadId);
				}

				if (event.transport === 'native') {
					this.nativeUploadLifecycles.delete(event.uploadId);
				}
			},
		);
		actor.on(
			'session.external.qr.shouldClose',
			() => {
				this.externalUpload.closeQRDialog();
			},
		);

		actor.start();
		this.sessionActor = actor;

		return actor;
	}

	private completeSavedSessionItem(uploadId: string): void {
		const photoModel = this.completedPhotoModelsByUploadId.get(uploadId);
		const uploadModel = UploadModule.findCompleted(uploadId);

		if (!photoModel) {
			throw new Error(`Missing completed photo model for upload ${uploadId}`);
		}

		if (!uploadModel) {
			throw new Error(`Missing completed upload model for upload ${uploadId}`);
		}

		this.completedPhotoModelsByUploadId.delete(uploadId);
		this.fileComplete(
			uploadModel,
			photoModel,
		);
	}

	/**
	 * Reports an upload-phase item failure and terminalizes publisher progress
	 * only when that item cannot retry.
	 *
	 * @param transport Adapter transport that produced the failure.
	 * @param uploadId Correlated item actor identifier.
	 * @param failure Structured item failure, or `null` for an internal failure.
	 */
	private handleUploadFailure(
		transport: UploadSessionTransport,
		uploadId: string,
		failure: UploadError | null,
	): void {
		this.failLocalBatchIfTerminal(
			uploadId,
			failure,
		);
		const error = new Error(failure?.message ?? 'Upload failed');
		const uploadModel = UploadModule.find(uploadId);

		if (uploadModel?.photoModelId) {
			const photoModel = PhotosModule.getById(uploadModel.photoModelId);

			if (photoModel) {
				PhotosModule.updateModel({
					id: photoModel.id,
					_error: error,
				});
			}
		}

		reportError(
			error,
			transport === 'native' ? 'error' : 'warning',
			(event) => {
				event.addMetadata(
					'uploadData',
					createUploadDiagnosticMetadata(
						uploadModel,
						{
							failureKind: (
								failure?.kind
								?? 'unknown'
							),
							retryable: (
								failure?.retryable
								?? false
							),
							stage: 'transferring',
							transport,
							uploadId,
						},
					),
				);
			},
		);
	}

	/**
	 * Reports a processing-phase item failure and preserves the current batch
	 * while the failed item still has retry budget.
	 *
	 * @param transport Adapter transport that produced the processing failure.
	 * @param uploadId Correlated item actor identifier.
	 * @param failure Structured item failure, or `null` for an internal failure.
	 */
	private handleUploadProcessingFailure(
		transport: UploadSessionTransport,
		uploadId: string,
		failure: UploadError | null,
	): void {
		this.failLocalBatchIfTerminal(
			uploadId,
			failure,
		);
		const error = new Error(
			failure?.message ?? 'Upload processing failed',
		) as PI.PhotoProcessingError;

		if (failure?.kind === 'processing') {
			error.retryable = failure.retryable;

			if (failure.code) {
				error.code = failure.code;
			}
		}

		const uploadModel = UploadModule.find(uploadId);

		if (uploadModel?.photoModelId) {
			const photoModel = PhotosModule.getById(uploadModel.photoModelId);

			if (photoModel) {
				PhotosModule.updateModel({
					id: photoModel.id,
					_error: error,
				});
			}
		}

		/*
		 * Transient processing failures (server hiccup on setTemporaryUploadUrl,
		 * etc.) need to stay visible in Bugsnag the same way the legacy pipeline
		 * surfaced them. NonRetryable processing errors are deterministic
		 * invariant failures (missing url, mismatched photo model) and would just
		 * spam the dashboard, so they stay silent.
		 */
		if (
			failure?.kind === 'processing'
			&& failure.retryable
		) {
			reportError(
				error,
				'warning',
				(event) => {
					event.addMetadata(
						'uploadData',
						createUploadDiagnosticMetadata(
							uploadModel,
							{
								failureKind: failure.kind,
								retryable: failure.retryable,
								stage: 'processing',
								transport,
								uploadId,
							},
						),
					);
				},
			);
		}
	}

	/**
	 * Promotes an item failure to the publisher's batch-level terminal state
	 * only after the item is non-retryable or its actor has exhausted retries.
	 *
	 * @param uploadId Correlated item actor identifier.
	 * @param failure Structured item failure, or `null` for an internal failure.
	 */
	private failLocalBatchIfTerminal(
		uploadId: string,
		failure: UploadError | null,
	): void {
		const itemActor = this.getSessionActor().getSnapshot().context.itemActors[uploadId];
		const canRetry = itemActor?.getSnapshot().can({
			type: 'upload.retry',
		}) ?? false;

		if (failure?.retryable !== false && canRetry) {
			return;
		}

		if (this.externalUpload.failLocalBatch(toUploadFailureReason(failure))) {
			this.clearProgressPoll();
		}
	}

	private runWebUploadAdapter(
		uploadId: string,
		lifecycle: UploadFileAdapterLifecycle,
	) {
		this.getSessionActor().send({
			type: 'session.item.metadata.update',
			uploadId,
			patch: {
				uploadStart: new Date().getTime(),
			},
		});

		const mapItem = this.fileTypeMapper.find((m) => m.id == uploadId);

		if (!mapItem) {
			lifecycle.fail({
				kind: 'upload',
				message: 'Could not locate upload model',
				retryable: false,
			});
			return;
		}

		/*
		 * AwsAutoExpireUploader listens to lifecycle.abortSignal internally, so
		 * item-actor cleanup propagates straight to UploadClient.abort() without
		 * a separate cleanup handle on this side.
		 */
		void this.getAwsUploader().runUpload(
			{
				id: uploadId,
				file: mapItem.file,
				fileName: mapItem.fileName,
				fileType: mapItem.fileType,
			},
			lifecycle,
		)
			.then((url) => {
				lifecycle.complete(url);
			})
			.catch((error: unknown) => {
				if (lifecycle.abortSignal.aborted) {
					return;
				}

				const retryable = (
					error instanceof Error
						&& 'retryable' in error
						&& typeof (error as { retryable?: unknown }).retryable === 'boolean'
				)
					? (error as { retryable: boolean }).retryable
					: true;

				lifecycle.fail({
					kind: 'upload',
					message: error instanceof Error ? error.message : String(error),
					retryable,
				});
			});
	}

	private getAwsUploader(): AwsAutoExpireUploader {
		if (!this.awsUploader) {
			this.awsUploader = new AwsAutoExpireUploader({
				cognito: awsCognito,
				getBucketModel: () => this.bucketModel,
				getConfig: () => ({
					'upload.accelerate': ConfigModule['upload.accelerate'],
				}),
			});
		}

		return this.awsUploader;
	}

	/**
	 * Snapshot the destination before asynchronous file inspection or native selection.
	 *
	 * @param batchSize Number of newly selected input files.
	 * @returns Design-print destinations, or undefined for another offering type.
	 */
	private getDesignPrintUploadContext(batchSize: number): PI.PhotoModel['_designPrint'] {
		const target = this.designPrintUploadTarget;
		this.designPrintUploadTarget = undefined;

		if (ProductStateModule.getOffering?.type !== 'design' || AppStateModule.uploadOnly) {
			/** A QR publisher has no authoritative editor destination on this device. */
			return undefined;
		}

		const pageId = target?.pageId ?? ProductStateModule.getActivePage?.id;
		const pageIds = getDesignPrintTargetPageIds(
			ProductStateModule.getEditablePages,
			pageId,
		);
		const context = createDesignPrintUploadContext(
			batchSize,
			pageIds,
		);
		const selectedObject = ProductStateModule.getSelectedPageObject;

		if (context) {
			context.targetPageObjectId = target
				? target.targetPageObjectId
				: (
					selectedObject && ProductStateModule.getActivePage?.id === pageId
						? selectedObject.id
						: undefined
				);
		}

		return context;
	}

	private runNativeUploadAdapter(
		uploadId: string,
		lifecycle: UploadFileAdapterLifecycle,
	) {
		this.nativeUploadLifecycles.set(
			uploadId,
			lifecycle,
		);

		return () => {
			this.nativeUploadLifecycles.delete(uploadId);
		};
	}

	private async completeUploadProcessing(
		uploadId: string,
		uploadUrl: string | null,
	) {
		/*
		 * Processing is not cancellable: the offline pause stops the XState invoke
		 * but the in-flight setTemporaryUploadUrl request keeps running, and on
		 * success it swaps the photo's local id for the remote one. If that
		 * orphaned run completed during the pause, re-processing on reconnect would
		 * look up the now-gone local id and fail ("Could not find photo model"),
		 * wedging an item whose photo actually saved. Short-circuit to the cached
		 * result so a resumed processing is idempotent against a completed prior run.
		 */
		const alreadyCompleted = this.completedPhotoModelsByUploadId.get(uploadId);

		if (alreadyCompleted && typeof alreadyCompleted.id === 'number') {
			return {
				remotePhotoId: alreadyCompleted.id,
			};
		}

		if (!uploadUrl) {
			throw new NonRetryableUploadProcessingError('Missing url of uploaded file');
		}

		const session = this.getSessionActor();

		session.send({
			type: 'session.item.metadata.update',
			uploadId,
			patch: {
				url: uploadUrl,
				uploadEnd: new Date().getTime(),
			},
		});

		const uploadModel = UploadModule.find(uploadId);

		if (!uploadModel) {
			throw new NonRetryableUploadProcessingError('Could not find upload model');
		}

		const photoModel = await this.processUploadedFileAsync(uploadModel);

		if (typeof photoModel.id !== 'number') {
			throw new NonRetryableUploadProcessingError('Processed upload did not resolve to a remote photo model');
		}

		this.completedPhotoModelsByUploadId.set(
			uploadId,
			photoModel,
		);

		return {
			remotePhotoId: photoModel.id,
		};
	}

	/**
	 * Inspect a local image and attach pre-upload correlation only on failure.
	 *
	 * @param file Local upload source.
	 * @param mapItem Resolved upload mapping and planned S3 identity.
	 * @returns Browser-derived dimensions, EXIF details, and preview usability.
	 */
	private getImageDetails(
		file: File,
		mapItem: FileTypeMap,
	): Promise<ImageDetails> {
		return detectImageDetails(
			file,
			{
				maxWidth: fullUploadSize.width,
				maxHeight: fullUploadSize.height,
				faceRecognition: experiment.isFeatureEnabled('flag_face_detection'),
				failureDiagnostics: {
					autoExpireBucket: this.bucketModel?.label,
					autoExpireObjectKey: buildAutoExpireObjectKey(mapItem),
					autoExpireUploadState: 'planned',
					declaredMime: (
						file.type
						|| 'unknown'
					),
					header: mapItem.headerDiagnostics,
					mimeSource: mapItem.mimeSource,
					resolvedMime: mapItem.fileType,
					sniffedMime: mapItem.sniffedMime,
					sourceKind: 's3',
					stage: 'upload-detail-probe',
					uploadId: mapItem.id,
				},
			},
			mapItem.fileType,
		);
	}

	/**
	 * Resolve file MIME/header data and register its local upload mapping.
	 *
	 * @param file Local file selected by the user.
	 * @returns Accepted file data shared by validation and upload setup.
	 */
	private async resolveFileData(file: File): Promise<FileData> {
		const resolved = await resolveFileType(
			file,
			this.acceptedUploadFileTypes.mimeTypes,
		);

		this.fileTypeMapper.push({
			id: nanoid(),
			file: resolved.file,
			fileName: resolved.file.name,
			fileType: resolved.fileType,
			headerDiagnostics: resolved.headerDiagnostics,
			lastModified: resolved.file.lastModified,
			mimeSource: resolved.mimeSource,
			size: resolved.file.size,
			sniffedMime: resolved.sniffedMime,
		});

		return resolved;
	}

	/**
	 * Installs native callbacks and normalizes cumulative picker results into new upload batches.
	 */
	private setupNativeBridge(): void {
		if (!window.nativeToWeb) {
			throw new Error('Missing native bridge');
		}

		window.nativeToWeb.setPickedFiles = (files, pickedOptions): void => {
			if (
				pickedOptions
				&& 'sortMode' in pickedOptions
			) {
				ProductStateModule.changeProductSettings({
					sortMode: pickedOptions.sortMode,
				});
			}

			if (files.length && files[0].objectLocation) {
				const { objectLocation } = files[0];
				AppStateModule.setNativeObjectLocation(objectLocation);
			}

			const uniqueNativeFileKeys = new Set<string>();
			const filesToAdd = files.filter((file) => {
				const nativeFileKey = `${file.source}:${file.id}`;
				const alreadySelected = (
					(
						file.source == 'upload'
						&& file.id.startsWith('pid')
					)
					|| Boolean(_.findWhere(
						ProductStateModule.getPhotosSelected,
						{
							source: file.source,
							externalId: file.id,
						},
					))
					|| uniqueNativeFileKeys.has(nativeFileKey)
				);

				uniqueNativeFileKeys.add(nativeFileKey);
				return !alreadySelected;
			});
			const batchSelection: UploadBatchSelection = {
				batchId: nanoid(),
				newCount: filesToAdd.length,
				rejectedCount: 0,
				selectedTotal: files.length,
				skippedExistingCount: files.length - filesToAdd.length,
				source: AppStateModule.uploadOnly
					? 'external-device'
					: 'native-picker',
			};
			const designPrint = this.getDesignPrintUploadContext(filesToAdd.length);

			if (filesToAdd.length === 0) {
				this.uploadSubmit(0);
				this.uploadSubmitted(
					0,
					batchSelection,
				);
				return;
			}

			const selections = filesToAdd.map(async (file): Promise<NativeAcceptedSelection | null> => {
				const nativeImgId = file.id;
				const { source } = file;

				/*
				 * Estimation: width x height x 3 (rgb) and divided by JPEG 2.5 compression factor
				 */
				const estimatedTotalBytes = (file.width * file.height * 3) / 2.5;
				const totalBytes = Number.isFinite(estimatedTotalBytes)
					? Math.max(
						1,
						estimatedTotalBytes,
					)
					: 1;
				const uploadStart = new Date().getTime();

				try {
					const photoModel = await ProductStateModule.selectPhoto({
						_designPrint: designPrint,
						_localRef: file.url,
						_orientation: file.orientation,
						source,
						externalId: nativeImgId,
						full_width: file.width,
						full_height: file.height,
						thumb_url: `${file.url}?width=250&height=250`,
						url: `${file.url}?width=${ConfigModule.photoPreviewSizeLocal}&height=${ConfigModule.photoPreviewSizeLocal}`,
						photodate: file.photodate
							? moment(file.photodate).format('X')
							: null,
						fcx: file.fcx,
						fcy: file.fcy,
						fcw: file.fcw,
						fch: file.fch,
						type: ProductStateModule.getOffering?.type || 'photo',
					});

					return {
						photoModel,
						totalBytes,
						uploadId: nativeImgId,
						uploadStart,
					};
				} catch (error: unknown) {
					reportError(
						error instanceof Error ? error : new Error(String(error)),
						'error',
						(event) => {
							event.addMetadata(
								'nativeUpload',
								{
									uploadId: nativeImgId,
								},
							);
						},
					);
					return null;
				}
			});

			void Promise.all(selections).then((selectionResults) => {
				const acceptedSelections = selectionResults.filter(
					(selection): selection is NativeAcceptedSelection => selection !== null,
				);
				const acceptedCount = acceptedSelections.length;
				batchSelection.newCount = acceptedCount;
				batchSelection.rejectedCount = filesToAdd.length - acceptedCount;

				this.uploadSubmit(
					acceptedCount,
					batchSelection.batchId,
					batchSelection,
				);
				acceptedSelections.forEach(({
					photoModel,
					totalBytes,
					uploadId,
					uploadStart,
				}) => {
					const localPhotoId = typeof photoModel.id === 'string'
						? photoModel.id
						: undefined;

					this.submitCount += 1;
					this.progressBatchUploadIds.add(uploadId);
					this.getSessionActor().send({
						type: 'session.item.add',
						item: {
							input: {
								localPhotoId,
								name: '',
								totalBytes,
								transport: 'native',
								uploadId,
							},
							metadata: {
								height: photoModel.full_height,
								photoModelId: photoModel.id,
								uploadStart,
								width: photoModel.full_width,
							},
							transport: 'native',
						},
					});
				});
				this.uploadSubmitted(
					acceptedCount,
					batchSelection,
				);
			});
		};

		window.nativeToWeb.onUploadFailed = (id) => {
			const lifecycle = this.nativeUploadLifecycles.get(id);

			if (lifecycle) {
				lifecycle.fail({
					kind: 'upload',
					message: 'Something went wrong uploading file',
					retryable: true,
				});
				return;
			}

			if (this.routeNativeBridgeEventToActor(
				id,
				{
					type: 'upload.failed',
					error: {
						kind: 'upload',
						message: 'Something went wrong uploading file',
						retryable: true,
					},
				},
			)) {
				return;
			}

			/*
			 * Only look at active queues — skip completed. A late native bridge
			 * failure event must NOT retroactively mark a successfully-completed
			 * upload's photoModel as errored.
			 */
			const uploadModel = (
				UploadModule.findError(id)
				|| UploadModule.findUploading(id)
				|| UploadModule.findWaiting(id)
			);

			if (uploadModel?.photoModelId) {
				const photoModel = PhotosModule.getById(uploadModel.photoModelId);

				if (photoModel) {
					PhotosModule.updateModel({
						id: photoModel.id,
						_error: new Error('Something went wrong uploading file'),
					});
				}
			}

			reportError(
				new Error('Upload failed'),
				'error',
				(event) => {
					event.addMetadata(
						'uploadData',
						createUploadDiagnosticMetadata(
							uploadModel,
							{
								failureKind: 'upload',
								retryable: true,
								stage: 'transferring',
								transport: 'native',
								uploadId: id,
							},
						),
					);
				},
			);
		};

		window.nativeToWeb.onUploadProgress = (id, bytesUploaded, actualFileSize) => {
			const lifecycle = this.nativeUploadLifecycles.get(id);

			if (lifecycle) {
				lifecycle.reportProgress(
					bytesUploaded,
					actualFileSize,
				);
				return;
			}

			if (this.routeNativeBridgeEventToActor(
				id,
				{
					type: 'upload.progress',
					uploadedBytes: bytesUploaded,
					totalBytes: actualFileSize,
				},
			)) {
				return;
			}

			reportError(
				new Error('Native upload progress event arrived after the session item was discarded'),
				'warning',
				(event) => {
					event.addMetadata(
						'uploadProgress',
						{
							uploadId: id,
							bytesUploaded,
							actualFileSize,
						},
					);
				},
			);
		};

		window.nativeToWeb.onUploadSucceeded = (id, url) => {
			const lifecycle = this.nativeUploadLifecycles.get(id);

			if (lifecycle) {
				lifecycle.complete(url);
				return;
			}

			if (this.routeNativeBridgeEventToActor(
				id,
				{
					type: 'upload.completed',
					uploadUrl: url,
				},
			)) {
				return;
			}

			/*
			 * No lifecycle handle and the item actor was already discarded, yet
			 * the native side reports a successful upload. Mirror the onUploadFailed
			 * fallback: recover by writing the result straight onto the photoModel
			 * so a success that races item discard is not silently dropped. We only
			 * look at active queues — a completion for an item already in the
			 * completed bucket has nothing left to recover.
			 */
			const uploadModel = (
				UploadModule.findError(id)
				|| UploadModule.findUploading(id)
				|| UploadModule.findWaiting(id)
			);

			if (uploadModel?.photoModelId) {
				const photoModel = PhotosModule.getById(uploadModel.photoModelId);

				if (photoModel) {
					PhotosModule.updateModel({
						id: photoModel.id,
						_error: undefined,
						full_url: url,
					});
				}
			}

			reportError(
				new Error('Native upload completion arrived after the session item was discarded'),
				'warning',
				(event) => {
					event.addMetadata(
						'uploadData',
						createUploadDiagnosticMetadata(
							uploadModel,
							{
								failureKind: 'unknown',
								retryable: false,
								stage: 'transferring',
								transport: 'native',
								uploadId: id,
							},
						),
					);
				},
			);
		};
	}

	private routeNativeBridgeEventToActor(
		uploadId: string,
		event:
			| { type: 'upload.failed';
				error: UploadError }
			| { type: 'upload.progress';
				uploadedBytes: number;
				totalBytes: number }
			| { type: 'upload.completed';
				uploadUrl: string },
	): boolean {
		const actor = this.sessionActor?.getSnapshot().context.itemActors[uploadId];

		if (!actor) {
			return false;
		}

		this.getSessionActor().send({
			type: 'session.item.send',
			uploadId,
			event,
		});

		return true;
	}

	private setupWeb() {
		if (!this.bucketModel || !this.bucketModel.label) {
			throw new Error('Missing required auto-expire bucket model');
		}

		if (ConfigModule['upload.fileSizeLimit']) {
			this.sizeLimit = ConfigModule['upload.fileSizeLimit'];
		}

		this.buttonEl = document.getElementById('uploadButton');

		if (this.buttonEl) {
			this.inputField = document.createElement('input');
			this.inputField.multiple = true;
			this.inputField.id = nanoid();
			this.inputField.type = 'file';

			this.buttonEl.appendChild(this.inputField);

			this.inputField.addEventListener(
				'change',
				() => {
					if (
						this.trackFilesChange
						&& this.inputField
						&& this.inputField.files
					) {
						const selectedFiles = this.inputField.files;
						this.addFiles(selectedFiles);
					}
				},
			);
		}
	}

	public filePicker(
		max: number | undefined,
		nativeOptions: {
			// Do we open up the native component by showing the current selection?
			showSelection: boolean;
			// At what bookmark do we open the photo gallery?
			objectLocation?: NativeObjectLocation;
			// Do we remove unselected photos in the native component from the project?
			removeUnselected: boolean;
		},
		event?: MouseEvent,
	) {
		if (window.glPlatform === 'native') {
			if (!window.webToNative || !window.webToNative.onFilePickerClicked) {
				throw new Error('Missing native FilePicker hook');
			}
			if (!this.bucketModel) {
				throw new Error('Missing required auto-expire bucket model');
			}
			if (!this.bucketModel.label) {
				throw new Error('Missing required label property of bucket model');
			}
			if (!this.bucketModel.region) {
				throw new Error('Missing required region property of bucket model');
			}

			const productModel = ProductStateModule.getProduct;

			if (!productModel) {
				throw new Error('Missing required product model');
			}

			const selectedFiles: NativeSelectedFile[] = [];

			if (!max || max > 1) {
				ProductStateModule.getPhotosSelected.forEach((photoModel) => {
					selectedFiles.push({
						id: photoModel.externalId || `pid${photoModel.id}`,
						source: photoModel.source || 'upload',
						url_full: photoModel.full_url || '',
						url_web: photoModel.url || '',
						url_thumb: photoModel.thumb_url || '',
					});
				});
			}

			const productSettings = ProductStateModule.getProductSettings;
			const isSortable = (!max || max > 1)
				&& OfferingGroups(
					productModel.group,
					[
						'BookTypes',
						'PhotoSheets',
					],
				)
				&& typeof productSettings.autoFill === 'undefined';
			let sortMode: PI.ProductSettings['sortMode'] = isSortable ? 'Auto' : '';

			if (ProductStateModule.productSettings.sortMode) {
				sortMode = ProductStateModule.productSettings.sortMode;
			}

			const showSelection = !!(nativeOptions && nativeOptions.showSelection);

			AppStateModule.setNativeSettingsMemory({
				removeUnselected: !!(nativeOptions && nativeOptions.removeUnselected && selectedFiles.length),
			});

			const maxNrOfImages = max
				? max + selectedFiles.length // The native picker needs to have the current selection included in the max
				: 200;

			window.webToNative.onFilePickerClicked({
				maxNrOfImages,
				maxImageWidth: fullUploadSize.width,
				maxImageHeight: fullUploadSize.height,
				selectedFiles,
				awsBucketName: this.bucketModel.label,
				awsRegion: this.bucketModel.region,
				sortMode,
				showSelection,
				objectLocation: JSON.parse(JSON.stringify(
					nativeOptions?.objectLocation
					?? {},
				)),
			});
		} else if (this.inputField) {
			this.trackFilesChange = true;

			this.inputField.accept = this.acceptedUploadFileTypes.accept;
			this.inputField.multiple = Boolean(!max || max !== 1);

			if (event?.defaultPrevented) {
				this.inputField.addEventListener(
					'click',
					(e) => {
						e.stopPropagation();
					},
					{
						once: true,
					},
				);
			}

			this.inputField.click();
		}
	}

	public get acceptedUploadFileTypes(): ReturnType<typeof getAcceptedUploadFileTypes> {
		return getAcceptedUploadFileTypes({
			eps: ConfigModule['upload.eps'],
			heic: ConfigModule['upload.heic'],
			pdf: ConfigModule['upload.pdf'],
			svg: ConfigModule['upload.svg'],
		});
	}

	/**
	 * Records analytics for every saved upload while exposing only photos owned
	 * by the active progress batch to its poll and completion event.
	 *
	 * @param uploadModel Saved upload metadata containing the batch-local upload id.
	 * @param photoModel Remote photo model produced by upload processing.
	 */
	private fileComplete(
		uploadModel: UploadModel,
		photoModel: PI.PhotoModel,
	): void {
		if (this.progressBatchUploadIds.has(uploadModel.id)) {
			this.completedPhotoModels.push(photoModel);
		}

		const offeringModel = ProductStateModule.getOffering;

		// Track analytics event
		const eventProperties: Record<string, any> = {
			fileSizeKb: uploadModel.totalBytes / 1000,
			platform: window.glPlatform,
		};

		if (
			uploadModel.uploadEnd
			&& uploadModel.uploadStart
		) {
			eventProperties.uploadTime = (uploadModel.uploadEnd - uploadModel.uploadStart) / 1000;
		}
		if (offeringModel) {
			eventProperties.groupid = offeringModel.groupid;
		}
		if (
			offeringModel
			&& uploadModel.width
			&& uploadModel.height
		) {
			// Set a flag to indicate if this combination of offering and photo could benefit from using the upscaling service
			const oW = offeringModel.width * (offeringModel.qualitydpi / offeringModel.configdpi);
			const oH = offeringModel.height * (offeringModel.qualitydpi / offeringModel.configdpi);

			eventProperties.couldUseUpscaling = uploadModel.width < oW || uploadModel.height < oH;
		}

		analytics.trackEvent(
			'Photo uploaded',
			eventProperties,
			{
				/*
				 * Since this is a heavily used analytics event,
				 * we sample it (for some labels) to reduce the amount of data
				 * thereby avoiding hitting the limits of the analytics service
				 */
				moengage: Math.random() < ConfigModule['analytics.moengage.samplingRate'],
				amplitude: Math.random() < ConfigModule['analytics.amplitude.samplingRate'],
			},
		);

		if (
			UploadModule.waiting.length === 0
			&& UploadModule.uploading.length === 0
			&& ProductStateModule.getPhotosFailed.length === 0
		) {
			// Trigger event to indicate that all files are uploaded to auto-expire bucket
			EventBus.emit(
				'upload complete',
				getDesignPrintUploadOutputs(
					this.completedPhotoModels,
					(id) => PhotosModule.getById(id),
				),
			);
		}
	}

	/**
	 * Tears down upload actors, progress tracking, and transient upload mappings.
	 */
	public reset(): void {
		this.designPrintUploadTarget = undefined;
		this.clearProgressPoll();
		this.progressBatchGeneration += 1;
		this.progressFinalizationActive = false;
		this.externalUpload.dispose();

		if (window.glPlatform === 'native') {
			if (!window.webToNative) {
				throw new Error('Missing WebToNative on window');
			}

			window.webToNative.cancelUploads();
		}

		this.sessionActor?.send({
			type: 'session.reset',
		});

		// Remove all mappings (forget previously uploaded files)
		this.fileTypeMapper = [];
		this.completedPhotoModelsByUploadId.clear();
		this.nativeUploadLifecycles.clear();
		this.progressBatchTransferredBaseline = 0;
		this.progressBatchUploadIds.clear();

		if (this.inputField) {
			this.inputField.value = '';
		}
	}

	/**
	 * Clear the failed-photo marker only after its owning transport accepts a
	 * retry. Rejected permanent failures must remain visible for removal.
	 *
	 * @param uploadModel Upload queue model whose retry was accepted.
	 */
	private clearPhotoProcessingError(uploadModel: UploadModel): void {
		if (!uploadModel.photoModelId) {
			return;
		}

		const photoModel = PhotosModule.getById(uploadModel.photoModelId);

		if (photoModel) {
			PhotosModule.updateModel({
				id: photoModel.id,
				_error: undefined,
			});
		}
	}

	/**
	 * Retry every upload currently projected in the error bucket.
	 * @param options.reset - User-initiated retry: reset exhausted retry budgets
	 * so capped items can attempt again. Automatic callers (save poll, reconnect)
	 * omit this and stay capped.
	 * @returns Whether at least one upload item accepted the retry request.
	 */
	public retryAll({ reset = false }: { reset?: boolean } = {}): boolean {
		/*
		 * Snapshot the current error queue. retry() drives the session machine,
		 * which moves items between buckets — iterating the live list while
		 * the projection shifts beneath us was the source of the prior
		 * length-check loop.
		 */
		const errorModels = [...UploadModule.error];
		let retryAccepted = false;

		errorModels.forEach((uploadModel) => {
			retryAccepted = this.retry(
				uploadModel,
				{
					reset,
				},
			) || retryAccepted;
		});

		if (ProductStateModule.getPhotosFailed.length) {
			void ProductStateModule.retryPhotoErrors({
				reset,
			});
		}

		return retryAccepted;
	}

	/**
	 * Retry a single failed upload through the native bridge or upload session.
	 * @param uploadModel - Upload queue model to retry.
	 * @param options.reset - User-initiated retry: clear the exhausted retry
	 * budget so a capped item can attempt again after a transient backend outage.
	 * Automatic retries omit this and stay count-guarded.
	 * @returns Whether the retry request was accepted by the owning transport.
	 */
	public retry(
		uploadModel: UploadModel,
		{ reset = false }: { reset?: boolean } = {},
	): boolean {
		const retryEventType = reset
			? 'upload.retry.reset'
			: 'upload.retry';

		if (window.glPlatform === 'native') {
			if (!window.webToNative) {
				throw new Error('Missing WebToNative on window');
			}

			const sessionSnapshot = this.sessionActor?.getSnapshot();
			const actor = sessionSnapshot?.context.itemActors[uploadModel.id];

			if (actor) {
				const snapshot = actor.getSnapshot();

				if (snapshot.can({
					type: retryEventType,
				})) {
					this.getSessionActor().send({
						type: 'session.item.retry',
						uploadId: uploadModel.id,
						reset,
					});

					if (snapshot.matches('uploadFailed')) {
						window.webToNative.retryUpload(uploadModel.id);
					}

					this.clearPhotoProcessingError(uploadModel);

					return true;
				} else {
					/*
					 * The item actor exists but has exhausted its retries (or is in
					 * a state that cannot accept upload.retry), so this retry request
					 * is a no-op for the user. Keep that state visible without creating
					 * a Bugsnag error event or exposing the upload model.
					 */
					leaveUploadBreadcrumb(
						'upload retry rejected',
						{
							...createUploadDiagnosticMetadata(
								uploadModel,
								{
									failureKind: 'unknown',
									retryable: false,
									stage: 'retrying',
									transport: 'native',
									uploadId: uploadModel.id,
								},
							),
							reset,
						},
					);
				}

				return false;
			} else {
				window.webToNative.retryUpload(uploadModel.id);
				this.clearPhotoProcessingError(uploadModel);
				return true;
			}
		} else {
			const sessionSnapshot = this.sessionActor?.getSnapshot();
			const existingActor = sessionSnapshot?.context.itemActors[uploadModel.id];

			if (existingActor) {
				if (
					!existingActor.getSnapshot().can({
						type: retryEventType,
					})
				) {
					leaveUploadBreadcrumb(
						'upload retry rejected',
						{
							...createUploadDiagnosticMetadata(
								uploadModel,
								{
									failureKind: 'unknown',
									retryable: false,
									stage: 'retrying',
									transport: 'web',
									uploadId: uploadModel.id,
								},
							),
							reset,
						},
					);

					return false;
				}

				this.getSessionActor().send({
					type: 'session.item.retry',
					uploadId: uploadModel.id,
					reset,
				});
				this.clearPhotoProcessingError(uploadModel);
				return true;
			} else {
				this.uploadFile(uploadModel);
				this.clearPhotoProcessingError(uploadModel);
				return true;
			}
		}
	}

	/**
	 * Retrieve a selected local file only when browser inspection considers it
	 * usable for previews.
	 *
	 * @param uploadId Opaque id assigned when the file was selected.
	 * @returns The local File, or undefined when missing or unsuitable to preview.
	 */
	public getLocalFile(uploadId: string): File | undefined {
		const mapItem = this.fileTypeMapper.find(
			(m) => m.id == uploadId,
		);

		/*
		 * Withhold files that require conversion or failed local inspection:
		 * serving the raw copy would produce an invalid browser preview.
		 */
		if (
			mapItem
			&& !mapItem.previewUnusable
		) {
			return mapItem.file;
		}

		return undefined;
	}

	/**
	 * Check whether an existing upload mapping has an unusable local preview.
	 *
	 * This is intentionally separate from {@link getLocalFile}: an undefined
	 * file can also mean the mapping was removed after upload completion, while
	 * this signal means remote fallbacks must wait for the local model to save.
	 *
	 * @param uploadId Opaque id assigned when the file was selected.
	 * @returns True only for a mapped file marked `previewUnusable`.
	 */
	public isLocalPreviewUnusable(uploadId: string): boolean {
		const mapItem = this.fileTypeMapper.find((m) => (
			m.id == uploadId
		));

		return mapItem?.previewUnusable === true;
	}

	public cancelUpload({ id }: {
		id?: string;
	}) {
		let uploadModel: UploadModel | undefined;

		if (id) {
			uploadModel = UploadModule.find(id);
		}

		if (!uploadModel) {
			return;
		}

		/*
		 * UploadModule.find also searches the completed bucket, so a cancel
		 * request for an already-finished item would otherwise emit a spurious
		 * session remove / native cancel for something that no longer needs it.
		 * Nothing to cancel once the upload has completed — no-op.
		 */
		if (UploadModule.findCompleted(uploadModel.id)) {
			return;
		}

		/*
		 * Validate native bridge availability BEFORE touching session/batch
		 * state. If the bridge is missing on a native build, we want to fail
		 * loudly without leaving the web side in an inconsistent half-cancel.
		 */
		if (window.glPlatform === 'native' && !window.webToNative) {
			throw new Error('Missing WebToNative on window');
		}

		const sessionSnapshot = this.sessionActor?.getSnapshot();
		const hasActor = Boolean(sessionSnapshot?.context.itemActors[uploadModel.id]);

		if (hasActor) {
			this.getSessionActor().send({
				type: 'session.item.send',
				uploadId: uploadModel.id,
				event: {
					type: 'upload.remove',
				},
			});
		} else {
			sessionSnapshot?.context.batchRef.send({
				type: 'item.removed',
				id: uploadModel.id,
			});
		}

		if (window.glPlatform === 'native') {
			window.webToNative!.cancelUpload(uploadModel.id);
			return;
		}

		// Remove from mapper, so the user could submit it again
		const i = this.fileTypeMapper.findIndex(
			(m) => m.id == id,
		);

		if (i >= 0) {
			this.fileTypeMapper.splice(
				i,
				1,
			);
		}
	}

	private async processUploadedFileAsync(uploadModel: UploadModel): Promise<PI.PhotoModel> {
		if (!uploadModel.photoModelId || !uploadModel.url) {
			throw new Error('Missing upload data for processing');
		}

		// Add photo to selected photos collection
		const photoModel = await PhotosModule.setTemporaryUploadUrl({
			id: uploadModel.photoModelId,
			url: uploadModel.url,
		});

		return photoModel;
	}

	public showQRCode(): Promise<void> {
		return this.externalUpload.showQRCode();
	}

	/**
	 * Preserve an editor target before the file picker or another upload UI opens.
	 *
	 * @param pageId Page receiving the first generated PDF photo.
	 * @param targetPageObjectId Existing selected object, when this is a replacement.
	 */
	public setDesignPrintUploadTarget(
		pageId: PI.PageModel['id'],
		targetPageObjectId?: PI.PageObjectModel['id'],
	): void {
		this.designPrintUploadTarget = {
			pageId,
			targetPageObjectId,
		};
	}

	/**
	 * Routes local or peer progress through correlation, ordering, and dialog ownership.
	 *
	 * @param data Progress payload to project.
	 * @param fromExternalUpload Whether AppSync delivered the progress from a peer.
	 * @param localProgress Optional batch-local stage and exact transferred item count.
	 */
	public updateProgress(
		data: UploadProgressMessage,
		fromExternalUpload: boolean,
		localProgress?: LocalUploadProgressContext,
	): void {
		this.externalUpload.updateProgress(
			data,
			fromExternalUpload,
			localProgress,
		);
	}

	/**
	 * Reopens an unexpired external batch as reconnecting after a reload.
	 *
	 * @returns Whether a scoped recovery marker restored a dialog.
	 */
	public restoreExternalUploadProgress(): boolean {
		return this.externalUpload.restoreExternalUploadProgress();
	}

	/** Projects an AppSync disconnect into the active progress dialog. */
	public markExternalUploadConnectionLost(): void {
		this.externalUpload.markExternalConnectionLost();
	}

	/** Restores the last confirmed stage after AppSync resubscribes. */
	public markExternalUploadConnectionRecovered(): void {
		this.externalUpload.markExternalConnectionRecovered();
	}

	/**
	 * Projects legacy aggregate counters onto a user-visible lifecycle stage.
	 * Once every known batch byte is transferred, the UI switches to processing
	 * and saving labels instead of presenting a frozen percentage bar.
	 *
	 * @param batchSize Number of accepted photos expected to finish processing.
	 * @returns Current local lifecycle projection.
	 */
	private getLocalUploadProgressStage(batchSize: number): UploadProgressStage {
		if (this.getUploadProgressPercentage < 100) {
			return 'transferring';
		}

		if (this.completedPhotoModels.length < batchSize) {
			return 'processing';
		}

		return 'saving';
	}

	/**
	 * Starts upload projection for a newly accepted batch.
	 *
	 * @param batchSize - Number of new files in this batch.
	 * @param batchId - Optional picker identity propagated to peer progress.
	 * @param batchSelection - Optional normalized picker totals for telemetry.
	 */
	private uploadSubmit(
		batchSize: number,
		batchId?: string,
		batchSelection?: UploadBatchSelection,
	): void {
		this.trackFilesChange = false;

		if (batchSize > 0) {
			this.completedPhotoModels = [];
			this.progressBatchTransferredBaseline = 0;
			this.progressBatchUploadIds.clear();
			this.progressBatchGeneration += 1;
			this.progressFinalizationActive = false;
			ProductStateModule.flagChange();
			this.externalUpload.startLocalBatch(
				batchId,
				batchSelection,
			);
		}

		if (batchSize > 1) {
			// Show progress dialog for the validation of the submitted files
			this.closeValidationDialog();

			this.validationDialog = DialogService.openProgressDialog({
				header: {
					title: window.App.router.$t('uploadStatusValidating'),
				},
				body: {
					props: {
						value: 0,
						total: batchSize,
					},
					listeners: {
						complete: () => {
							this.closeValidationDialog();
						},
					},
				},
			});
		}

		// Trigger event to indicate start of processing new selected upload files
		EventBus.emit(
			'upload submit',
			batchSize,
		);
	}

	/**
	 * Reports transfer progress for the active batch only. Processing and saving
	 * are named indeterminate stages, so unrelated project-wide queued photos no
	 * longer distort the percentage or create a synthetic 99% hold.
	 *
	 * @returns Active batch byte progress from zero through one hundred.
	 */
	public get getUploadProgressPercentage(): number {
		const uploadBatchSnapshot = this.sessionActor?.getSnapshot().context.batchRef.getSnapshot();
		const activeBatchItems = uploadBatchSnapshot
			? Object.fromEntries(
				Object.entries(uploadBatchSnapshot.context.items).filter(
					([uploadId]) => this.progressBatchUploadIds.has(uploadId),
				),
			)
			: {};

		if (
			uploadBatchSnapshot
			&& Object.keys(activeBatchItems).length > 0
		) {
			return getUploadBatchProgress({
				...uploadBatchSnapshot.context,
				items: activeBatchItems,
			});
		}

		if (this.progressBatchUploadIds.size > 0) {
			return 0;
		}

		if (UploadModule.totalBytes > 0) {
			return Math.min(
				100,
				Math.max(
					0,
					100 * UploadModule.totalUploadBytes / UploadModule.totalBytes,
				),
			);
		}

		return 100;
	}

	/**
	 * Counts fully transferred items in the active batch without projecting the
	 * aggregate byte percentage onto differently sized files.
	 *
	 * @returns Number of active-batch items whose byte transfer is complete.
	 */
	private getActiveBatchTransferredCount(): number {
		const uploadBatchSnapshot = this.sessionActor?.getSnapshot().context.batchRef.getSnapshot();

		if (!uploadBatchSnapshot) {
			return this.progressBatchTransferredBaseline;
		}

		return this.progressBatchTransferredBaseline
			+ Object.entries(uploadBatchSnapshot.context.items).filter(([
				uploadId,
				item,
			]) => (
				this.progressBatchUploadIds.has(uploadId)
				&& item.totalBytes > 0
				&& item.uploadedBytes >= item.totalBytes
			)).length;
	}

	/**
	 * Finalizes validation for one batch and starts progress projection for accepted files.
	 *
	 * @param batchSize - Number of new files accepted into the batch.
	 * @param batchSelection - Native picker totals used for correlated batch analytics.
	 */
	private uploadSubmitted(
		batchSize: number,
		batchSelection?: UploadBatchSelection,
	): void {
		// Automatically close the progress dialog in case that is displayed
		const dialogProgressComponent = this.validationDialog?.api.bodyComponent();

		if (dialogProgressComponent) {
			dialogProgressComponent.value = dialogProgressComponent.total;
		}

		if (this.inputField) {
			this.inputField.value = '';
		}

		const skippedFilesMessage = formatSkippedFiles(
			this.skippedFiles,
			this.sizeLimit,
		);

		if (skippedFilesMessage) {
			if (this.skippedFiles.size.length > 0) {
				leaveUploadBreadcrumb(
					'upload files rejected by size validation',
					{
						rejectedCount: this.skippedFiles.size.length,
						sizeLimit: this.sizeLimit,
					},
				);
			}

			const closeError = DialogService.openErrorDialog({
				body: {
					content: skippedFilesMessage,
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('dialogButtonErrorOk'),
							click: () => {
								this.skippedFiles = createSkippedFiles();
								this.uploadSubmitted(
									batchSize,
									batchSelection,
								);
								closeError();
							},
						},
					],
				},
			});
		} else if (this.checkMaxPhotos && this.rejectBatchSizeCount > 0) {
			// Check if batch size exceeds the photo limit for the product
			const { themeModel } = ThemeStateModule;
			const maxphotos = !themeModel || _.isNull(themeModel.maxphotos)
				? 200
				: themeModel.maxphotos;

			const closeError = DialogService.openErrorDialog({
				body: {
					content: window.App.router.$t(
						'uploadErrorTooMaanyPhotos',
						{
							maxphotos,
							skipcount: this.rejectBatchSizeCount,
						},
					),
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('dialogButtonErrorOk'),
							click: () => {
								this.rejectBatchSizeCount = 0;
								this.uploadSubmitted(
									batchSize,
									batchSelection,
								);
								closeError();
							},
						},
					],
				},
			});
		} else {
			/*
			 * Trigger event to indicate that all new submitted upload are either accepted or
			 * rejected by the upload controller
			 */
			if (batchSelection) {
				EventBus.emit(
					'upload batch submitted',
					batchSelection,
				);
			}

			EventBus.emit(
				'upload submitted',
				batchSize,
			);

			this.startProgressPoll(batchSize);
		}
	}

	/**
	 * Restarts publisher progress after native state has rebuilt the active item
	 * actors. Reconciliation already restored the protocol identity and latest
	 * sequence in ExternalUploadController, so this path must not emit a fresh
	 * selected tick or create a new batch.
	 *
	 * @param batch Authoritative native snapshot accepted during reconciliation.
	 */
	private resumeNativeBatchProgress(batch: NativeUploadReconciliationBatch): void {
		const { progress } = batch;

		if (
			progress.submittedCount <= 0
			|| progress.stage === 'completed'
			|| progress.stage === 'failed'
			|| progress.stage === 'aborted'
		) {
			return;
		}

		const completedPhotoModels = (progress.photos ?? [])
			.map((photo) => PhotosModule.getById(photo.id));

		if (!completedPhotoModels.every(
			(photoModel): photoModel is PI.PhotoModel => typeof photoModel !== 'undefined',
		)) {
			throw new Error('Reconciled native batch contains an unavailable completed photo');
		}

		this.completedPhotoModels = completedPhotoModels;
		this.progressBatchTransferredBaseline = completedPhotoModels.length;
		this.progressBatchUploadIds = new Set(batch.items.map((item) => item.uploadId));
		this.progressBatchGeneration += 1;
		this.progressFinalizationActive = false;
		this.startProgressPoll(
			progress.submittedCount,
			false,
		);
	}

	/**
	 * Starts the batch-local progress publisher used by fresh and reconciled
	 * native uploads.
	 *
	 * @param batchSize Number of items expected to reach saved.
	 * @param publishSelected Whether to publish the fresh-batch selected stage.
	 */
	private startProgressPoll(
		batchSize: number,
		publishSelected = true,
	): void {
		if (batchSize <= 0) {
			return;
		}

		/*
		 * Overlapping batches (or a reset) must not leak the prior poll.
		 * Clear any in-flight interval before starting a new one and track
		 * the handle on the instance so reset() can stop it too.
		 */
		this.clearProgressPoll();
		const progressBatchGeneration = this.progressBatchGeneration;

		if (publishSelected) {
			this.updateProgress(
				{
					progressPercentage: 0,
					submittedCount: batchSize,
					photos: [],
				},
				false,
				{
					stage: 'selected',
					transferredCount: 0,
				},
			);
		}

		this.progressPollHandle = window.setInterval(
			() => {
				const stage = this.getLocalUploadProgressStage(batchSize);

				this.updateProgress(
					{
						progressPercentage: this.getUploadProgressPercentage,
						submittedCount: batchSize,
					},
					false,
					{
						stage,
						transferredCount: this.getActiveBatchTransferredCount(),
					},
				);

				if (
					this.completedPhotoModels.length >= batchSize
					&& !this.progressFinalizationActive
				) {
					this.progressFinalizationActive = true;
					this.clearProgressPoll();
					const completedPhotoModels = getDesignPrintUploadOutputs(
						this.completedPhotoModels.slice(
							0,
							batchSize,
						),
						(id) => PhotosModule.getById(id),
					);
					const photos: UploadProgressPhoto[] = [];

					const arrPromises: Promise<string | File>[] = [];
					completedPhotoModels.forEach((photoModel) => {
						arrPromises.push(
							PhotosModule.getModelUrl({
								id: photoModel.id,
								resolution: 'thumb',
								forceRemote: true,
							}),
						);
					});

					Promise.allSettled(arrPromises).then((results) => {
						if (progressBatchGeneration !== this.progressBatchGeneration) {
							return;
						}

						completedPhotoModels.forEach((photoModel, i) => {
							const promiseResult = results[i];

							if (
								promiseResult.status === 'fulfilled'
								&& typeof promiseResult.value === 'string'
							) {
								photos.push({
									id: photoModel.id,
									url: promiseResult.value,
								});
							} else {
								photos.push({
									id: photoModel.id,
								});
							}
						});

						this.updateProgress(
							{
								progressPercentage: 100,
								submittedCount: batchSize,
								photos,
							},
							false,
							{
								stage: 'completed',
								transferredCount: batchSize,
							},
						);
						this.externalUpload.closeProgressDialog('publisher');
						this.progressFinalizationActive = false;
					});
				} else if (
					ProductStateModule.getPhotosQueued.length
					&& AppStateModule.online
					&& !AppStateModule.sync
				) {
					/* Save process seems to be stuck so give it a kickstart. */
					ProductState.save().catch((error) => {
						/*
						 * Best-effort kickstart: the expected "Unsaved photo models"
						 * reject in dialog-suppressed flows (import/select/overview)
						 * is a no-op here. Surface anything else so real save
						 * failures stay visible.
						 */
						if (error instanceof UnsavedPhotoModelsError) {
							return;
						}

						reportError(
							error,
							'warning',
						);
					});
				}
			},
			100,
		);
	}

	public waitForFileConversion(
		options?: FileConversionWaitOptions,
	): Promise<'settled' | 'timeout'> {
		return waitForFileConversion(
			options,
			{
				dialogs: {
					fileConversion: FileConversionView,
					vectorization: VectorizationView,
				},
				isLocalPreviewUnusable: (localRef) => this.isLocalPreviewUnusable(localRef),
			},
		);
	}
}

export default new Upload();
